import { createFileRoute } from "@tanstack/react-router";
import { Alert } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import {
  ImageField,
  InputField,
  LightSelectField,
  SelectField,
  TextAreaField,
} from "@/components/ui/form";
import PhoneNumberField from "@/components/ui/form/phone-number-field";
import { useSimpleForm } from "@/hooks/use-simple-form";
import { useTranslate } from "@/hooks/use-translate";
import useAppStore from "@/store";
import { Country, State } from "country-state-city";
import { ChevronLeft } from "lucide-react";
import { useEffect } from "react";

export const Route = createFileRoute("/(dasboard)/settings")({
  component: SettingsPage,
});

function SettingsPage() {
  const store = useAppStore();
  const { t } = useTranslate();

  const form = useSimpleForm({
    // Shop settings
    currency: "",
    language: "",
    invoice_prefix: "",
    receipt_prefix: "",
    receipt_format: "A5",
    receipt_message: "",

    // Location + shop contact
    name: "",
    phone_number: "",
    email: "",
    country: "",
    region: "",
    city: "",
    address: "",

    // Images
    logo: { file: undefined, src: "" },
  });

  useEffect(() => {
    const shop = store.auth.session?.currentShop;
    if (!shop) return;

    store.shop.fetchOne(shop.id).then((shop) => {
      const location: any = shop.location ?? {};

      form.setValues({
        currency: shop.currency ?? "",
        language: shop.language ?? "",
        invoice_prefix: shop.invoice_prefix ?? "",
        receipt_prefix: shop.receipt_prefix ?? "",
        receipt_format: shop.receipt_format ?? "A5",
        receipt_message: shop.receipt_message ?? "",

        name: shop.name ?? "",
        phone_number: shop.phone_number ?? "",
        email: shop.email ?? "",

        country: location.country ?? "",
        region: location.region ?? "",
        city: location.city ?? "",
        address: location.address ?? "",

        logo: {
          file: undefined,
          src: shop.logo?.url ?? "",
        },
      });

      if (location["country"] == undefined || location["country"] == "") {
        form.setValue("country", "CM");
        form.setValue("region", State.getStatesOfCountry("CM")[0]?.name);
      }
    });
  }, [store.auth.session]);

  const submit = () => {
    store.errors.reset();
    store.shop
      .update(store.auth.session?.currentShop?.id as string, form.getFormData())
      .then(() => {
        window.scrollTo({
          top: 0,
          behavior: "smooth",
        });
        store.display.show("settings_update_success");
        localStorage.setItem("locale", form.values.language);
        window.location.reload();
      })
      .catch(store.errors.catch);
  };

  return (
    <>
      <Alert
        autoClose={3000}
        message={t("Settings updated successfully")}
        name={"settings_update_success"}
      />

      <section className="max-w-4xl mx-auto">
        <div>
          <button
            type="button"
            onClick={() => window.history.back()}
            className="py-2 inline-flex gap-2 items-center justify-center rounded-lg text-sm text-gray-500 hover:text-gray-900 hover:underline font-semibold"
          >
            <ChevronLeft className="h-4 w-4" />
            <span>{t("Back")}</span>
          </button>
        </div>

        <div className="mt-4 mb-4 flex justify-between">
          <h2 className="text-xl font-semibold">{t("Settings")}</h2>
        </div>

        <>{store.errors.render()}</>

        <div className="bg-white p-6 rounded-md border border-gray-200">
          <div>
            {/* Logo */}
            <div>
              <ImageField
                label={t("Shop Logo")}
                name="logo"
                image={form.getImageValue("logo")}
                onImageChange={form.handleImageChange("logo")}
                error={store.errors.values.logo}
                id="logo"
                className="h-64 w-64 border-dashed"
              />
            </div>

            {/* Basic info */}
            <div className="mt-4 grid grid-cols-1 md:grid-cols-2 gap-4">
              <InputField
                label={t("Shop Name")}
                name="name"
                value={form.values.name}
                onChange={form.handleChange}
                error={store.errors.values.name}
              />

              <div>
                <label className="mb-1.5 block text-sm font-medium text-gray-900">
                  {t("Phone number")}
                </label>
                <PhoneNumberField
                  value={form.values.phone_number}
                  onValueChange={(v) => form.setValue("phone_number", v)}
                  error={store.errors.values.phone_number}
                />
              </div>

              <InputField
                label={t("Email address")}
                name="email"
                type="email"
                value={form.values.email}
                onChange={form.handleChange}
                error={store.errors.values.email}
              />

              {/* Country */}
              <SelectField
                label={t("Country")}
                name="country"
                value={form.values.country}
                onChange={form.handleChange}
                options={Country.getAllCountries().map((c) => ({
                  label: c.name,
                  value: c.isoCode,
                }))}
              />

              {/* Region */}
              <SelectField
                label={t("State/Region")}
                name="region"
                value={form.values.region}
                onChange={form.handleChange}
                options={
                  form.values.country
                    ? State.getStatesOfCountry(form.values.country).map(
                        (s) => ({
                          label: s.name,
                          value: s.isoCode,
                        })
                      )
                    : []
                }
              />

              <InputField
                label={t("City")}
                name="city"
                value={form.values.city}
                onChange={form.handleChange}
                error={store.errors.values.city}
              />

              <InputField
                label={t("Street address")}
                name="address"
                value={form.values.address}
                onChange={form.handleChange}
                error={store.errors.values.address}
              />
            </div>

            <div className="my-6 border-t border-gray-200"></div>

            {/* Shop Settings */}
            <div className="gap-4 mt-6 grid grid-cols-2">
              <LightSelectField
                label={t("Language")}
                name="language"
                value={form.values.language}
                onChange={form.handleChange}
                error={store.errors.values.language}
                options={[
                  { label: t("English"), value: "en" },
                  { label: "French", value: "fr" },
                ]}
              />

              <InputField
                label={t("Currency")}
                name="currency"
                value={form.values.currency}
                onChange={form.handleChange}
                error={store.errors.values.currency}
              />

              <TextAreaField
                label={t("Receipt bottom message")}
                name="receipt_message"
                value={form.values.receipt_message}
                onChange={form.handleChange}
                error={store.errors.values.receipt_message}
              />

              {/* <InputField
                label="Invoice Prefix"
                name="invoice_prefix"
                value={form.values.invoice_prefix}
                onChange={form.handleChange}
                error={store.errors.values.invoice_prefix}
              />

              <InputField
                label="Receipt Prefix"
                name="receipt_prefix"
                value={form.values.receipt_prefix}
                onChange={form.handleChange}
                error={store.errors.values.receipt_prefix}
              /> */}

              {/* <LightSelectField
                label="Receipt Format"
                name="receipt_format"
                value={form.values.receipt_format}
                onChange={form.handleChange}
                error={store.errors.values.receipt_format}
                options={[
                  { label: "A5", value: "A5" },
                  { label: "A6", value: "A6" },
                ]}
              /> */}
            </div>
          </div>
        </div>

        <Button className="mt-6 px-6 py-2" onClick={submit}>
          {t("Update Settings")}
        </Button>
      </section>
    </>
  );
}
